import { c as _c } from "react/compiler-runtime";
import { type CSSObject } from '@precis-digital/kurama/theme';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import { TableHeaderCell } from '@precis-digital/kurama/Table';
import { flexRender } from '@tanstack/react-table';
import { useCallback, type PointerEventHandler, type ReactElement } from 'react';
import { useChartCardDefaults } from '../../../hooks/useChartCardDefaults';
import { CHART_CARD_TABLE_LAYOUT } from '../../../constants';
import type { ChartDataRow } from '../../../types';
import { COLUMN_ID_DATA_ATTR, Z_INDEX } from '../constants';
import type { TableHeadCellContentProps, TableHeadCellDnDProps, TableHeadCellProps } from '../types';
import { calculateCSSPadding, getColumnCellStyle, getHeaderContext, getPinPositionForColumnId } from '../utils';
import TableHeaderCellContent from './TableHeadCellContent';
type HTMLDivPointerElement = HTMLDivElement & {
  addEventListener: (type: string, listener: (event: HTMLDivPointerEvent) => void) => void;
  removeEventListener: (type: string, listener: (event: HTMLDivPointerEvent) => void) => void;
};
type HTMLDivPointerEvent = PointerEvent & {
  currentTarget: HTMLDivPointerElement;
};
const TableHeadCell = props => {
  const $ = _c(36);
  const defaults = useChartCardDefaults();
  const theme = useTheme();
  const {
    header,
    columnPinning,
    isResizingColumn,
    resizerCursorUrl,
    rowHovered,
    allowColumnResizing,
    columnDnDCellRef,
    columnDnDIsDropTarget,
    columnDnDIsDragging,
    columnDnDDragHandle
  } = props;
  const {
    id: headerId,
    isPlaceholder,
    column,
    getResizeHandler
  } = header;
  let allowResizing;
  let cellContent;
  let t0;
  if ($[0] !== allowColumnResizing || $[1] !== column || $[2] !== columnDnDDragHandle || $[3] !== columnPinning || $[4] !== defaults || $[5] !== props || $[6] !== rowHovered || $[7] !== theme) {
    const context = props.context ?? getHeaderContext(props);
    cellContent = context == null ? null : flexRender(TableHeaderCellContent, {
      ...context,
      columnDnDDragHandle
    });
    allowResizing = context == null ? allowColumnResizing : context.meta.chartCard.allowColumnResizing;
    const pinned = getPinPositionForColumnId(columnPinning, column.id);
    t0 = getColumnCellStyle({
      defaults,
      theme,
      column,
      allowResizing,
      pinned,
      type: "head",
      rowHovered
    });
    $[0] = allowColumnResizing;
    $[1] = column;
    $[2] = columnDnDDragHandle;
    $[3] = columnPinning;
    $[4] = defaults;
    $[5] = props;
    $[6] = rowHovered;
    $[7] = theme;
    $[8] = allowResizing;
    $[9] = cellContent;
    $[10] = t0;
  } else {
    allowResizing = $[8];
    cellContent = $[9];
    t0 = $[10];
  }
  const cellStyle = t0;
  let t1;
  if ($[11] !== column) {
    t1 = () => {
      column.resetSize();
    };
    $[11] = column;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const onDoubleClickHandler = t1;
  let t2;
  if ($[13] !== getResizeHandler) {
    t2 = event => {
      event.currentTarget.setPointerCapture(event.pointerId);
      const pointerUpHandler = event_0 => {
        event_0.currentTarget.removeEventListener("pointerup", pointerUpHandler);
        event_0.currentTarget.releasePointerCapture(event_0.pointerId);
        getResizeHandler()(event_0);
      };
      event.currentTarget.addEventListener("pointerup", pointerUpHandler);
      getResizeHandler()(event);
    };
    $[13] = getResizeHandler;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const onPointerDownHandler = t2;
  const t3 = `calc(var(--header-${headerId}-size) * 1px)`;
  const t4 = columnDnDIsDropTarget === true;
  const t5 = columnDnDIsDragging === true;
  let t6;
  if ($[15] !== column.id) {
    t6 = {
      [COLUMN_ID_DATA_ATTR]: column.id
    };
    $[15] = column.id;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== allowResizing || $[18] !== column.id || $[19] !== isPlaceholder || $[20] !== isResizingColumn || $[21] !== onDoubleClickHandler || $[22] !== onPointerDownHandler || $[23] !== resizerCursorUrl) {
    t7 = allowResizing && !isPlaceholder && <CellResizer className="resizer" data-resizing={isResizingColumn === column.id ? true : undefined} onDoubleClick={onDoubleClickHandler} onPointerDown={onPointerDownHandler} resizerCursorUrl={resizerCursorUrl} />;
    $[17] = allowResizing;
    $[18] = column.id;
    $[19] = isPlaceholder;
    $[20] = isResizingColumn;
    $[21] = onDoubleClickHandler;
    $[22] = onPointerDownHandler;
    $[23] = resizerCursorUrl;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== allowResizing || $[26] !== cellContent || $[27] !== cellStyle || $[28] !== columnDnDCellRef || $[29] !== headerId || $[30] !== t3 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7) {
    t8 = <StyledTableHeaderCell ref={columnDnDCellRef} allowResizing={allowResizing} key={headerId} width={t3} cellStyle={cellStyle} isDropTarget={t4} isDragging={t5} {...t6}>{cellContent}{t7}</StyledTableHeaderCell>;
    $[25] = allowResizing;
    $[26] = cellContent;
    $[27] = cellStyle;
    $[28] = columnDnDCellRef;
    $[29] = headerId;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
};
const CellResizer = styled('div', {
  shouldForwardProp: prop => prop !== 'resizerCursorUrl'
})<{
  resizerCursorUrl: string | null;
}>(({
  theme,
  resizerCursorUrl
}) => ({
  position: 'absolute',
  zIndex: Z_INDEX.head + 1,
  top: 0,
  height: `calc(100% - ${theme.spacing(3)})`,
  margin: `${theme.spacing(1)} 0`,
  right: 0,
  width: '5px',
  userSelect: 'none',
  touchAction: 'none',
  backgroundImage: `linear-gradient(to right, transparent 2px, ${theme.tokens.foreground.main.disabled} 2px, ${theme.tokens.foreground.main.disabled} 3px, transparent 3px)`,
  cursor: resizerCursorUrl == null ? 'col-resize' : `url(${resizerCursorUrl}) ${CHART_CARD_TABLE_LAYOUT.resizeIconSize / 2} ${CHART_CARD_TABLE_LAYOUT.resizeIconSize / 2}, col-resize`
}));
const StyledTableHeaderCell = styled(TableHeaderCell, {
  shouldForwardProp: prop => prop !== 'cellStyle' && prop !== 'width' && prop !== 'allowResizing' && prop !== 'isDropTarget' && prop !== 'isDragging'
})<{
  allowResizing: boolean;
  cellStyle: CSSObject;
  width?: number | string;
  isDropTarget?: boolean;
  isDragging?: boolean;
}>(({
  theme,
  allowResizing,
  cellStyle,
  width,
  isDropTarget,
  isDragging
}): CSSObject => ({
  display: 'flex',
  gap: theme.spacing(1),
  padding: calculateCSSPadding(CHART_CARD_TABLE_LAYOUT.padding.head, allowResizing ? undefined : {
    right: -5
  }),
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: theme.spacing(8),
  width,
  ...cellStyle,
  ...(isDropTarget === true && {
    boxShadow: `inset 0 0 0 2px ${theme.tokens.background.brand.main.default}`,
    zIndex: Z_INDEX.head + 1
  }),
  ...(isDragging === true && {
    opacity: 0.55
  }),
  '& .resizer[data-resizing]': {
    backgroundImage: 'none',
    backgroundColor: theme.tokens.background.brand.main.default
  }
}));
export default TableHeadCell;